import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import isEmpty from 'lodash/isEmpty';
import { Spinner } from 'reactstrap';
import classNames from 'classnames';

import teamService from '../../services/auth';
import { authActionTypes } from '@store/Auth/Auth.actionTypes';
import { useMemberCompanyTimezones } from '@hooks/useMemberCompanyTimezones';
import memberSessionsService from '@services/memberSessions';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import { getUserTimezone } from '@utils/timezone';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { getAppNameByAppPath } from '@/utils/utility';
import '@assets/scss/argon-dashboard/custom/body-container/_body-container.scss';
import { memberActionTypes } from '@store/Member/Member.actionTypes';
import { timelineActionTypes } from '@store/Timeline/Timeline.actionTypes';
import { useTeamMemberDetailsForSelect } from '@hooks/useTeamMemberDetailsForSelect';
import { Dropdown } from '@/components/Dropdown';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { TimeFilter } from '@/components/TimeFilter/TimeFilter.component';
import { IoRefresh } from 'react-icons/io5';
import { Button } from '@/components/Button';
import {
  ProductivityScoreCard,
  TotalTimeTrackedCard,
  IdleTimeCard,
} from '@/components/StatsCards';
import { EmptyState } from '@/components/EmptyState';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { parseSearchParams } from '@/utils/urlSearch';
import { getDashboardOverviewFiltersFromSearch } from '@/utils/dashboardOverviewUrlFilters';
import {
  useDashboardOverviewPopSync,
  useDashboardOverviewUrlWrite,
} from '@/hooks/useDashboardOverviewUrlSync';
import { appRoutes } from '@/routes/routes';
import { mergeReportFiltersIntoSearch } from '@/utils/dashboardOverviewUrlFilters';

import { queryKeys } from '../../config/queryKeys';
import { formatPercent } from '@/utils/numberUtils';
import { calculateStatCardData } from './DashboardOverview.helper';
import { filterScreenshotsByTimeRange } from '../Screenshots/Screenshots.helper';
import { getActivityColor } from '../Timeline/Timeline.helper';
import { TimelineChart } from '../Timeline/Chart';
import { normalizeIdleForDisplay } from '@/utils/idleDisplayUtils';
import {
  collectScreenshotsFromActivities,
  getScreenshotPreviewSrc,
  getScreenshotRevealKey,
} from '@/utils/screenshotPrivacy';

import styles from './DashboardOverview.module.scss';

// Timeline GET_SESSIONS page size for the selected date range.
const TIMELINE_SESSIONS_PAGE_SIZE = 1000;

// Cap eager session activity fetches on overview (screenshots strip).
const SCREENSHOT_STRIP_PREFETCH_SESSION_CAP = 8;

// Fallback when a screenshot URL fails to load.
const SCREENSHOT_PLACEHOLDER_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239ca3af' font-size='12'%3EImage not available%3C/text%3E%3C/svg%3E";

// Empty path → "Unknown"; otherwise `getAppNameByAppPath`.
function displayNameForAppPath(path, mapping) {
  if (!path) return 'Unknown';
  return getAppNameByAppPath(path, mapping);
}

// Member uid → billing rate and employee id for API payloads.
function buildMemberLookup(membersOptions) {
  const byId = new Map(membersOptions.map((m) => [m.value, m]));
  return {
    rateForUser: (userId) => byId.get(userId)?.rate ?? 0,
    employeeIdForUser: (userId) =>
      userId ? (byId.get(userId)?.employeeId ?? userId) : '',
  };
}

// V2 time-tracking totals → `memberTimeData` slice for stat cards.
function mapV2ResponseToReduxTimeData(data) {
  return {
    totalHours: data.totalHours || 0,
    totalMinutes: data.totalMinutes || 0,
    idleHours: data.idleHours || 0,
    idleMinutes: data.idleMinutes || 0,
    productiveHours: data.productiveHours || 0,
    productiveMinutes: data.productiveMinutes || 0,
  };
}

// Session start time for getSessionActivities (storage path resolution).
function findSessionStartTime(sessionsByDay, actualSessionId, sessionId) {
  if (!sessionsByDay) return null;
  for (const day of sessionsByDay) {
    const found = day.sessions?.find(
      (s) => s.id === actualSessionId || s.id === sessionId
    );
    if (found?.start) return found.start;
  }
  return null;
}

// Top row: member, timezone, date range, refresh.
function DashboardOverviewFiltersBar({
  membersOptions,
  selectedMember,
  onMemberChange,
  membersLoading,
  membersError,
  membersErrorMessage,
  onRetryMembers,
  timezoneOptions,
  selectedTimezone,
  onTimezoneChange,
  timezonesLoading,
  dateRange,
  setDateRange,
  onRefresh,
}) {
  const timezoneValue =
    timezoneOptions.find((t) => t.value === selectedTimezone) ||
    (selectedTimezone
      ? { value: selectedTimezone, label: selectedTimezone }
      : null);

  return (
    <div className={styles.filtersContainer}>
      <div className={styles.dropdownsContainer}>
        <div className={styles.dropdown}>
          <Dropdown
            options={membersOptions}
            value={selectedMember}
            label="SELECT MEMBER"
            onChange={onMemberChange}
            isLoading={membersLoading}
            isDisabled={membersLoading || membersError}
          />
          <div className={styles.memberErrorContainer}>
            {membersError && (
              <div className={styles.memberError}>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Medium}
                >
                  Failed to load members. {membersErrorMessage || ''}
                </Text>
                <Button color="link" size="sm" onClick={onRetryMembers}>
                  Retry
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className={styles.dropdown}>
          <Dropdown
            label="TIME"
            options={timezoneOptions}
            value={timezoneValue}
            onChange={({ value }) => onTimezoneChange(value)}
            isLoading={timezonesLoading}
          />
        </div>
      </div>
      <div className={styles.timeFilterContainer}>
        <TimeFilter range={dateRange} setRange={setDateRange} />
      </div>
      <div className={styles.refreshButton} onClick={onRefresh}>
        <IoRefresh />
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Medium}
        >
          Refresh
        </Text>
      </div>
    </div>
  );
}

// One session’s thumbnails (or loading / empty message).
function SessionScreenshotStrip({ screenshots, selectedTimezone, busy }) {
  if (busy) {
    return (
      <div className={styles.screenshotsLoading}>
        <Spinner size="sm" color="primary" />
        <span className="ml-2">Loading screenshots...</span>
      </div>
    );
  }
  if (screenshots?.length === 0) {
    return (
      <div className={styles.noScreenshots}>
        No screenshots available for this session.
      </div>
    );
  }

  return (
    <>
      {(screenshots ?? []).map((screenshot, idx) => {
        const rk = getScreenshotRevealKey(screenshot);
        return (
          <div key={rk || idx} className={styles.screenshotItem}>
            <div className={styles.imageContainer}>
              <img
                src={getScreenshotPreviewSrc(screenshot)}
                alt={`Screenshot ${idx + 1}`}
                className={styles.image}
                loading="lazy"
                onError={(e) => {
                  e.target.src = SCREENSHOT_PLACEHOLDER_SVG;
                  e.target.onerror = null;
                }}
              />
            </div>
            <div className={styles.footer}>
              <Text
                variant={textVariantsEnum.Text_5}
                fontWeight={fontWeightEnum.Semibold}
                className={styles.timeStamp}
              >
                {screenshot.timestamp
                  ? moment(screenshot.timestamp)
                      .tz(selectedTimezone)
                      .format('hh:mm A')
                  : null}
              </Text>
            </div>
          </div>
        );
      })}
    </>
  );
}

// Productivity score + total + idle stat cards.
function ProductivityPulseSection({
  productiveScore,
  totalTimeTrackedText,
  idleTimeText,
}) {
  return (
    <>
      <div className={styles.dashboardOverviewHeader}>
        <Text
          variant={textVariantsEnum.Display_xs}
          fontWeight={fontWeightEnum.Medium}
        >
          Productivity Pulse
        </Text>
        <Text
          variant={textVariantsEnum.Text_xs}
          fontWeight={fontWeightEnum.Medium}
        >
          Real-time overview of your performance and time flow.
        </Text>
      </div>
      <div className={styles.dashboardOverviewCardsContainer}>
        <div className={styles.dashboardOverviewCard}>
          <ProductivityScoreCard
            productivityScore={formatPercent(productiveScore)}
            contaninerClassName={styles.statCard}
            chartConfig={{
              backgroundColor: ['#121F5C', '#F3F6FC'],
            }}
          />
        </div>
        <div className={styles.dashboardOverviewCard}>
          <TotalTimeTrackedCard
            totalTimeTracked={totalTimeTrackedText}
            contaninerClassName={styles.statCard}
          />
        </div>
        <div className={styles.dashboardOverviewCard}>
          <IdleTimeCard
            idleTime={idleTimeText}
            contaninerClassName={styles.statCard}
          />
        </div>
      </div>
    </>
  );
}

// Per-day timeline bars; "View" links to /timeline with the same URL filters (member, range, tz).
function ActivityFlowSection({
  sessionsByDay,
  selectedTimezone,
  dateRange,
  timelinePath,
  reportFiltersSearch,
}) {
  return (
    <div className={styles.activityFlowContainer}>
      <div className={styles.activityFlowHeader}>
        <Text
          variant={textVariantsEnum.Header}
          fontWeight={fontWeightEnum.Semibold}
        >
          Activity Flow
        </Text>
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Normal}
          color={'gray-500'}
        >
          Timeline: {moment(dateRange.start).format('MMM DD, YYYY')} to{' '}
          {moment(dateRange.end).format('MMM DD, YYYY')}
        </Text>
        <Link
          to={{ pathname: timelinePath, search: reportFiltersSearch }}
          className={styles.viewAppUsage}
          style={{
            textDecoration: 'none',
            fontSize: '0.875rem',
            marginTop: '0.25rem',
          }}
        >
          View →
        </Link>
      </div>
      <div
        className={classNames(styles.activityFlowChartContainer, 'with-scroll')}
      >
        {!sessionsByDay?.length ? (
          <EmptyState />
        ) : (
          <>
            {sessionsByDay.map((daySession) => {
              const segments = daySession.sessions.map((session) => {
                const startTime = moment(session.start)
                  .tz(selectedTimezone)
                  .format('HH:mm');
                const endTime = moment(session.end)
                  .tz(selectedTimezone)
                  .format('HH:mm');
                const { displayIdle, idlePercent } = normalizeIdleForDisplay(
                  session.durationSec,
                  session.idleSec
                );
                return {
                  start12H: moment(session.start)
                    .tz(selectedTimezone)
                    .format('hh:mm A'),
                  end12H: moment(session.end)
                    .tz(selectedTimezone)
                    .format('hh:mm A'),
                  totalTimeTracked: formatTimeFromSeconds(session.durationSec),
                  idleTime: formatTimeFromSeconds(displayIdle),
                  activityPct: formatPercent(session.activityPct),
                  idlePercent: formatPercent(idlePercent),
                  start24H: startTime,
                  end24H: endTime,
                  color: getActivityColor(session.activityPct, true),
                };
              });
              const dayLabel =
                daySession.dayLabel ||
                (daySession.dayKey
                  ? moment(daySession.dayKey).format('ddd, MMM D, YYYY')
                  : '');
              return (
                <div
                  key={daySession.dayKey}
                  className={styles.daySessionContainer}
                >
                  <Text
                    variant={textVariantsEnum.Text_xs}
                    fontWeight={fontWeightEnum.Normal}
                    color={'gray-500'}
                    className={styles.dayLabel}
                  >
                    {dayLabel}
                  </Text>
                  <TimelineChart segments={segments} />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// App usage bars from precomputed rows (seconds + labels); View → opens full app usage with same URL filters.
function EcosystemUsageSection({ rows, appUsagePath, reportFiltersSearch }) {
  return (
    <div className={classNames(styles.ecoSystemContainer, 'with-scroll')}>
      <div className={styles.ecoSystemHeaderRow}>
        <Text
          variant={textVariantsEnum.Header}
          fontWeight={fontWeightEnum.Semibold}
          className={styles.ecoSystemHeader}
        >
          Ecosystem Usage
        </Text>
      </div>
      <div className={styles.ecoSystemUsageContainer}>
        {!rows?.length ? (
          <EmptyState />
        ) : (
          <>
            {rows.map((app) => (
              <div key={app.appPath} className={styles.appUsageBarContainer}>
                <div className={styles.appUsageBarLabel}>
                  <Text
                    variant={textVariantsEnum.Text_xs}
                    fontWeight={fontWeightEnum.Medium}
                  >
                    {app.appDisplayName}
                  </Text>
                  <Text
                    variant={textVariantsEnum.Text_xs}
                    fontWeight={fontWeightEnum.Medium}
                  >
                    {app.formattedTime}
                  </Text>
                </div>
                <div
                  className={styles.appUsageBar}
                  style={{ width: `${app.percentage}%` }}
                />
              </div>
            ))}
          </>
        )}
      </div>
      {/* <div className={styles.viewAppUsageLink}> */}
      <Link
        to={{ pathname: appUsagePath, search: reportFiltersSearch }}
        className={styles.viewAppUsageLink}
        style={{
          color: '#686868',
          textDecoration: 'none',
          fontSize: '0.675rem',
          marginTop: '0.25rem',
        }}
      >
        VIEW ALL APPLICATIONS
      </Link>
      {/* </div> */}
    </div>
  );
}

// Scrollable strip of session screenshot groups + link to Screenshots page.
function ScreenshotsSection({
  sessionsByDay,
  sessionScreenshots,
  selectedTimezone,
  screenshotsBusy,
  screenshotsPath,
  reportFiltersSearch,
}) {
  return (
    <div className={styles.screenshotsContainer}>
      <div className={styles.screenshotsHeader}>
        <Text
          variant={textVariantsEnum.Header}
          fontWeight={fontWeightEnum.Semibold}
        >
          Screenshots
        </Text>
        <Link
          to={{ pathname: screenshotsPath, search: reportFiltersSearch }}
          className="text-primary small"
          style={{
            textDecoration: 'none',
            fontSize: '0.875rem',
            marginTop: '0.25rem',
          }}
        >
          View →
        </Link>
      </div>
      <div className={classNames(styles.screenshotsRow, 'with-scroll')}>
        {!sessionsByDay?.length ? (
          <EmptyState />
        ) : (
          <>
            {sessionsByDay.map((daySession) =>
              daySession.sessions.map((individualSession) => {
                const screenshots =
                  sessionScreenshots[individualSession.id] || [];
                return (
                  <SessionScreenshotStrip
                    key={individualSession.id}
                    screenshots={screenshots}
                    selectedTimezone={selectedTimezone}
                    busy={screenshotsBusy}
                  />
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}

export const DashboardOverviewComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const team = useSelector((state) => state.team);
  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyId = userProfile?.activeCompany?.companyId;

  const timeData = useSelector((state) => state.member.memberTimeData);
  const { sessionsByDay } = useSelector((state) => state.timeline);

  const currentUserId = userProfile?.id;

  const [sessionScreenshots, setSessionScreenshots] = useState({});
  const [loadingScreenshots, setLoadingScreenshots] = useState({});
  const sessionScreenshotsRef = useRef(sessionScreenshots);
  const loadingScreenshotsRef = useRef(loadingScreenshots);
  const sessionActivitiesCacheRef = useRef({});
  // Latest maps for lazy screenshot loads (dedupe by session id).
  sessionScreenshotsRef.current = sessionScreenshots;
  loadingScreenshotsRef.current = loadingScreenshots;

  const getSessionActivitiesCacheKey = (sessionId, timezone) =>
    `${sessionId}::${timezone || 'UTC'}`;

  const { cid: qcid } = parseSearchParams(location.search);
  const cid = qcid || localStorage.getItem('cid');

  const {
    isSuccess,
    isFetched,
    data: userResponse,
  } = useQuery(
    queryKeys.users.detail(userProfile?.id),
    async () => {
      const { success, data } = await teamService.getCurrentUserDetails(
        userProfile?.id
      );
      if (success === true) return { success, data };
      throw new Error('Unable to load user data. Please try again later.');
    },
    { enabled: !!userProfile?.id }
  );

  const { productiveScore, totalTimeTrackedText, idleTimeText } =
    calculateStatCardData(timeData);

  const teamId = team?.active?.activeTeamid;
  const {
    memberDetails,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useTeamMemberDetailsForSelect(teamId);

  const membersOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .filter(([, m]) => !m?.deleted)
      .map(([uid, m]) => ({
        value: uid,
        label: m.employeeId
          ? `${m.userName || uid} (${m.employeeId})`
          : m.userName || uid,
        employeeId: m.employeeId,
        rate: m.rates ?? 0,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  const memberLookup = useMemo(
    () => buildMemberLookup(membersOptions),
    [membersOptions]
  );

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const filtersFromUrl = useMemo(
    () => getDashboardOverviewFiltersFromSearch(location.search),
    [location.search]
  );

  // User changed member or TZ: stop driving timezone from `tz` query param.
  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  // Manual TZ pick: block late async timezone from overwriting the dropdown.
  const dashboardTimezoneUserOverrideRef = useRef(false);

  // Seed from query string when present; otherwise today.
  const [dateRange, setDateRange] = useState(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const { start, end } = getDashboardOverviewFiltersFromSearch(search);
    const today = moment().format('YYYY-MM-DD');
    return {
      start: start || today,
      end: end || today,
    };
  });

  const [selectedMember, setSelectedMember] = useState(null);
  const [appUsage, setAppUsage] = useState({});
  const [loadingAppUsage, setLoadingAppUsage] = useState(false);
  const [appNameMapping, setAppNameMapping] = useState({});

  // Exec name → display label map for ecosystem usage (one fetch on mount).
  useEffect(() => {
    fetchApplicationInfoMapping().then(setAppNameMapping);
  }, []);

  useDashboardOverviewPopSync({
    history,
    setDateRange,
    setIgnoreUrlTimezone,
    setSelectedMember,
    membersOptionsRef,
  });

  // Save company id and align Redux `activeCompany` with the loaded user.
  useEffect(() => {
    if (!(cid && isFetched && isSuccess && userResponse)) return;

    localStorage.setItem('cid', cid);
    const uData = userResponse?.data;
    const companyList = uData?.companyList ?? [];
    const selectedCompany = companyList.filter((i) => i.companyId === cid)[0];

    dispatch({
      type: authActionTypes.SET_STATE,
      payload: {
        profile: {
          ...userProfile,
          role: selectedCompany ? selectedCompany?.role : companyList[0]?.role,
          companyList: !isEmpty(uData) ? companyList : [],
          activeCompany: selectedCompany,
        },
      },
    });
  }, [dispatch, isFetched, isSuccess, userResponse, cid]);

  const effectiveEmployeeId = useMemo(
    () =>
      memberLookup.employeeIdForUser(selectedMember?.value || currentUserId),
    [memberLookup, selectedMember?.value, currentUserId]
  );

  // `tz` from URL feeds the timezone hook until the user overrides.
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId: selectedMember?.value || currentUserId,
    companyId: activeCompanyId,
    initialTimezone: urlTimezoneForHook,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef: dashboardTimezoneUserOverrideRef,
  });

  useDashboardOverviewUrlWrite({
    teamId,
    memberId: selectedMember?.value,
    dateRange,
    selectedTimezone,
    history,
    location,
  });

  const clearMemberTimeData = useCallback(() => {
    dispatch({
      type: memberActionTypes.SET_STATE,
      payload: {
        memberTimeData: null,
        memberLoading: false,
      },
    });
  }, [dispatch]);

  const setMemberLoading = useCallback(
    (loading) => {
      dispatch({
        type: memberActionTypes.SET_STATE,
        payload: { memberLoading: loading },
      });
    },
    [dispatch]
  );

  const applyMemberTimeData = useCallback(
    (payload) => {
      dispatch({
        type: memberActionTypes.SET_STATE,
        payload,
      });
    },
    [dispatch]
  );

  const fetchSessions = useCallback(() => {
    if (!selectedMember) return;
    const employeeId = memberLookup.employeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId: activeCompanyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: dateRange.start,
        endDate: dateRange.end,
        timeZone: selectedTimezone,
        page: 1,
        pageSize: TIMELINE_SESSIONS_PAGE_SIZE,
        rate: memberLookup.rateForUser(selectedMember.value),
      },
    });
  }, [
    selectedMember,
    memberLookup,
    dispatch,
    teamId,
    activeCompanyId,
    dateRange.start,
    dateRange.end,
    selectedTimezone,
  ]);

  const fetchSessionsAndAppUsage = useCallback(async () => {
    if (
      !teamId ||
      !activeCompanyId ||
      !effectiveEmployeeId ||
      !dateRange.start ||
      !dateRange.end
    ) {
      setAppUsage({});
      clearMemberTimeData();
      return;
    }

    setLoadingAppUsage(true);
    setMemberLoading(true);

    try {
      let timezone = selectedTimezone;
      if (!timezone && effectiveEmployeeId) {
        try {
          const memberUid = selectedMember?.value || currentUserId;
          const tzInfo = await getUserTimezone(teamId, memberUid);
          timezone = tzInfo.timezone || moment.tz.guess();
        } catch (tzError) {
          console.warn('Failed to resolve member timezone:', tzError);
          timezone = moment.tz.guess();
        }
      }

      const response = await memberSessionsService.fetchTimeTrackingDataV2({
        teamId,
        companyId: activeCompanyId,
        userId: selectedMember?.value,
        startDate: dateRange.start,
        endDate: dateRange.end,
        timezone,
      });

      if (!response.success) {
        console.error('Failed to fetch time tracking data:', response.error);
        setAppUsage({});
        clearMemberTimeData();
        return;
      }

      const data = response.data;
      const memberTimeData = mapV2ResponseToReduxTimeData(data);

      setAppUsage(data.appUsage || {});

      applyMemberTimeData({
        memberTimeData,
        memberLoading: false,
      });
    } catch (error) {
      console.error('Error fetching time tracking data:', error);
      setAppUsage({});
      clearMemberTimeData();
    } finally {
      setLoadingAppUsage(false);
    }
  }, [
    teamId,
    activeCompanyId,
    selectedMember?.value,
    dateRange.start,
    dateRange.end,
    selectedTimezone,
    effectiveEmployeeId,
    currentUserId,
    clearMemberTimeData,
    setMemberLoading,
    applyMemberTimeData,
  ]);

  const refreshDashboardData = useCallback(() => {
    fetchSessions();
    fetchSessionsAndAppUsage();
  }, [fetchSessions, fetchSessionsAndAppUsage]);

  useEffect(() => {
    sessionActivitiesCacheRef.current = {};
    setSessionScreenshots({});
    setLoadingScreenshots({});
  }, [selectedMember?.value, dateRange.start, dateRange.end, selectedTimezone]);

  // Timeline sessions + V2 summary for the current filters.
  useEffect(() => {
    if (
      dateRange.start &&
      dateRange.end &&
      selectedMember?.value &&
      activeCompanyId
    ) {
      fetchSessions();
      fetchSessionsAndAppUsage();
    }
  }, [
    dateRange.start,
    dateRange.end,
    selectedMember?.value,
    activeCompanyId,
    fetchSessions,
    fetchSessionsAndAppUsage,
  ]);

  const fetchSessionScreenshots = useCallback(
    async (
      sessionId,
      parentSessionId = null,
      segmentStart = null,
      segmentEnd = null
    ) => {
      const shots = sessionScreenshotsRef.current;
      const loading = loadingScreenshotsRef.current;

      const actualSessionId = parentSessionId || sessionId;
      const isSplitSegment = parentSessionId && sessionId !== actualSessionId;

      if (isSplitSegment && shots[actualSessionId]) {
        const allScreenshots = shots[actualSessionId];
        const filteredScreenshots = filterScreenshotsByTimeRange(
          allScreenshots,
          segmentStart,
          segmentEnd,
          selectedTimezone
        );
        setSessionScreenshots((prev) => ({
          ...prev,
          [sessionId]: filteredScreenshots,
        }));
        return;
      }

      if (shots[actualSessionId] || loading[actualSessionId]) {
        return;
      }

      if (!teamId || !selectedMember?.value || !actualSessionId) {
        return;
      }

      const activitiesCacheKey = getSessionActivitiesCacheKey(
        actualSessionId,
        selectedTimezone
      );
      if (sessionActivitiesCacheRef.current[activitiesCacheKey]) {
        const cachedAll = sessionActivitiesCacheRef.current[activitiesCacheKey];
        setSessionScreenshots((prev) => {
          const updated = { ...prev, [actualSessionId]: cachedAll };
          if (isSplitSegment && segmentStart && segmentEnd) {
            updated[sessionId] = filterScreenshotsByTimeRange(
              cachedAll,
              segmentStart,
              segmentEnd,
              selectedTimezone
            );
          }
          return updated;
        });
        return;
      }

      setLoadingScreenshots((prev) => ({ ...prev, [actualSessionId]: true }));

      const sessionStartTime = findSessionStartTime(
        sessionsByDay,
        actualSessionId,
        sessionId
      );

      try {
        const employeeId = memberLookup.employeeIdForUser(selectedMember.value);
        const result = await memberSessionsService.getSessionActivities({
          teamId,
          userUID: selectedMember.value,
          companyId: activeCompanyId,
          employeeId,
          sessionId: actualSessionId,
          sessionStartTime,
        });

        if (result.success) {
          const activities = result.data.activities || [];
          const allScreenshots = collectScreenshotsFromActivities(activities);

          sessionActivitiesCacheRef.current[activitiesCacheKey] =
            allScreenshots;

          setSessionScreenshots((prev) => {
            const updated = { ...prev, [actualSessionId]: allScreenshots };

            if (isSplitSegment && segmentStart && segmentEnd) {
              updated[sessionId] = filterScreenshotsByTimeRange(
                allScreenshots,
                segmentStart,
                segmentEnd,
                selectedTimezone
              );
            }

            return updated;
          });
        }
      } catch (error) {
        console.error('Error loading session screenshots', error);
      } finally {
        setLoadingScreenshots((prev) => {
          const updated = { ...prev };
          delete updated[actualSessionId];
          if (isSplitSegment) {
            delete updated[sessionId];
          }
          return updated;
        });
      }
    },
    [
      selectedTimezone,
      teamId,
      selectedMember,
      activeCompanyId,
      sessionsByDay,
      memberLookup,
    ]
  );

  // Lazy-load screenshot metadata per session (capped — avoids N API calls for huge ranges).
  useEffect(() => {
    if (!sessionsByDay?.length) return;

    let prefetched = 0;
    for (const day of sessionsByDay) {
      for (const session of day.sessions) {
        if (!session.id) continue;
        if (prefetched >= SCREENSHOT_STRIP_PREFETCH_SESSION_CAP) return;

        const actualSessionId = session.parentSessionId || session.id;
        const isSplitSegment = session.isSplit && session.parentSessionId;
        const shots = sessionScreenshotsRef.current;
        const loading = loadingScreenshotsRef.current;

        if (!shots[actualSessionId] && !loading[actualSessionId]) {
          prefetched += 1;
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            isSplitSegment ? session.start : null,
            isSplitSegment ? session.end : null
          );
        } else if (
          isSplitSegment &&
          !shots[session.id] &&
          shots[actualSessionId]
        ) {
          fetchSessionScreenshots(
            session.id,
            session.parentSessionId,
            session.start,
            session.end
          );
        }
      }
    }
  }, [sessionsByDay, selectedTimezone, fetchSessionScreenshots]);

  // Default member: `member` query param, else current user, else first in list.
  useEffect(() => {
    if (!teamId) {
      setSelectedMember(null);
      return;
    }

    if (membersOptions.length === 0) return;
    if (selectedMember) return;

    const urlMemberId = filtersFromUrl.member;
    if (urlMemberId) {
      const fromUrl = membersOptions.find((m) => m.value === urlMemberId);
      if (fromUrl) {
        setSelectedMember(fromUrl);
        return;
      }
    }

    const defaultMember =
      membersOptions.find((m) => m.value === currentUserId) ||
      membersOptions[0];
    if (defaultMember) {
      setSelectedMember(defaultMember);
    }
  }, [
    teamId,
    membersOptions,
    currentUserId,
    selectedMember,
    filtersFromUrl.member,
  ]);

  const sortedAppUsage = useMemo(() => {
    return Object.entries(appUsage)
      .map(([appPath, seconds]) => {
        const sec = Number(seconds) || 0;
        return {
          appPath,
          appName: displayNameForAppPath(appPath, appNameMapping),
          seconds: sec,
          formattedTime: formatTimeFromSeconds(sec),
        };
      })
      .sort((a, b) => b.seconds - a.seconds);
  }, [appUsage, appNameMapping]);

  const sortedAppUsageByTime = useMemo(() => {
    const totalTime = sortedAppUsage.reduce(
      (acc, curr) => acc + curr.seconds,
      0
    );
    return sortedAppUsage.map((app) => ({
      ...app,
      appDisplayName: app.appName.split('/').pop(),
      percentage: totalTime ? (app.seconds / totalTime) * 100 : 0,
    }));
  }, [sortedAppUsage]);

  const handleMemberChange = useCallback((opt) => {
    dashboardTimezoneUserOverrideRef.current = false;
    setIgnoreUrlTimezone(true);
    setSelectedMember(opt);
  }, []);

  const handleTimezoneChange = useCallback(
    (value) => {
      dashboardTimezoneUserOverrideRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone]
  );

  const routes = useMemo(() => appRoutes(), []);
  const reportFiltersSearch = useMemo(
    () =>
      mergeReportFiltersIntoSearch(location.search, {
        memberId: selectedMember?.value,
        start: dateRange.start,
        end: dateRange.end,
        tz: selectedTimezone || '',
      }),
    [
      location.search,
      selectedMember?.value,
      dateRange.start,
      dateRange.end,
      selectedTimezone,
    ]
  );

  return (
    <div className={styles.dashboardOverviewPage}>
      <DashboardOverviewFiltersBar
        membersOptions={membersOptions}
        selectedMember={selectedMember}
        onMemberChange={handleMemberChange}
        membersLoading={membersLoading}
        membersError={membersError}
        membersErrorMessage={membersErrorObj?.message}
        onRetryMembers={() => refetchMembers()}
        timezoneOptions={timezoneOptions}
        selectedTimezone={selectedTimezone}
        onTimezoneChange={handleTimezoneChange}
        timezonesLoading={timezonesLoading}
        dateRange={dateRange}
        setDateRange={setDateRange}
        onRefresh={refreshDashboardData}
      />

      {loadingAppUsage ? (
        <div className={styles.loadingContainer}>
          <div className="text-center my-5">
            <Spinner color="primary" size="lg" />
            <p className="mt-3 text-muted">Loading time tracking data...</p>
          </div>
        </div>
      ) : (
        <div className={styles.dashboardOverviewContainer}>
          <ProductivityPulseSection
            productiveScore={productiveScore}
            totalTimeTrackedText={totalTimeTrackedText}
            idleTimeText={idleTimeText}
          />

          <div className={styles.container}>
            <ActivityFlowSection
              sessionsByDay={sessionsByDay}
              selectedTimezone={selectedTimezone}
              dateRange={dateRange}
              timelinePath={routes.timeline}
              reportFiltersSearch={reportFiltersSearch}
            />
            <EcosystemUsageSection
              rows={sortedAppUsageByTime}
              appUsagePath={routes.appUsage}
              reportFiltersSearch={reportFiltersSearch}
            />
          </div>

          <ScreenshotsSection
            sessionsByDay={sessionsByDay}
            sessionScreenshots={sessionScreenshots}
            selectedTimezone={selectedTimezone}
            screenshotsBusy={loadingAppUsage}
            screenshotsPath={routes.screenshots}
            reportFiltersSearch={reportFiltersSearch}
          />
        </div>
      )}
    </div>
  );
};
